/**
 * CharCreateScene.ts — Character creation flow (Ascend-style).
 *
 * Steps:
 * 1. Class menu — all 5 classes listed with symbols, colors, taglines
 * 2. Class detail — stats, description, choose/back
 * 3. Name entry — auto-generate or manual via InputPrompt
 * 4. Backstory intro — class-specific text with typewriter
 * 5. Transition — create GameState, initialize StoryEngine, enter StoryScene
 *
 * Migrated to FrameRenderer + PanelWriter for framed UI layout.
 */

import type { GameEngine, Scene } from '@core/engine/GameEngine';
import type { MenuOption } from '@ui/MenuSystem';
import type { ClassId, StatName } from '@game/data/types';
import type { ColorName } from '@ui/ColorTheme';
import { getClass, getClassIds } from '@game/data/classes';
import { StoryScene } from './StoryScene';
import { ErrorScene } from './ErrorScene';
import { analytics } from '@game/Analytics';

const STAT_ABBR: Record<StatName, string> = {
  strength: 'STR',
  dexterity: 'DEX',
  constitution: 'CON',
  intelligence: 'INT',
  wisdom: 'WIS',
  charisma: 'CHA',
};

export class CharCreateScene implements Scene {
  name = 'CharCreateScene';

  async enter(engine: GameEngine): Promise<void> {
    // Set arcane particles for character creation
    engine.getContext().visualLayer.setLocationParticles('arcane_sanctum');

    const selectedClass = await this.classSelection(engine);
    if (!selectedClass) {
      // Player backed out — return to title
      await engine.popScene();
      return;
    }

    const characterName = await this.nameEntry(engine, selectedClass);
    if (!characterName) {
      // Player backed out — restart class selection
      await this.enter(engine);
      return;
    }

    await this.backstoryIntro(engine, selectedClass, characterName);
    await this.createAndStart(engine, selectedClass, characterName);
  }

  async exit(engine: GameEngine): Promise<void> {
    engine.getContext().sprites.hide();
  }

  // ─── Class Selection ─────────────────────────────────────

  private async classSelection(engine: GameEngine): Promise<ClassId | null> {
    const ctx = engine.getContext();
    const { menu, frameRenderer, effects } = ctx;
    const classIds = getClassIds();

    while (true) {
      ctx.sprites.hide();

      // Draw two-column layout: left for menu, right for preview
      const layout = frameRenderer.drawFrame({
        title: 'CREATE YOUR HERO',
        headerRight: 'Choose a Class',
        borderColor: 'frostBlue',
        twoColumn: true,
        leftRatio: 0.40,
      });

      // Left panel: Class menu
      const leftWriter = frameRenderer.createWriter(layout.leftPanel!, effects);
      leftWriter.blankLine();
      leftWriter.writeCentered('CHOOSE YOUR CLASS', 'icyWhite');
      leftWriter.blankLine();

      const options: MenuOption[] = classIds.map(id => {
        const cls = getClass(id);
        return {
          label: `${cls.symbol}  ${cls.name}`,
          color: cls.color as ColorName,
        };
      });

      // Function to draw preview panel (uses current frame layout)
      const getStatRating = this.getStatRating;
      const drawPreviewPanel = (classIndex: number) => {
        if (classIndex < 0 || classIndex >= classIds.length) return;
        const classId = classIds[classIndex];
        const cls = getClass(classId);

        // Get current layout's right panel (may have been redrawn by onBeforeRender)
        const currentLayout = frameRenderer.getLayout();
        if (!currentLayout?.rightPanel) return;
        const rightWriter = frameRenderer.createWriter(currentLayout.rightPanel, effects);
        rightWriter.blankLine();
        rightWriter.writeLine(`  ${cls.name.toUpperCase()}`, cls.color as ColorName);
        rightWriter.writeLine('  ─────────────────', 'frostSilver');
        rightWriter.blankLine();
        rightWriter.writeLine(`  ${cls.description}`, 'white');
        rightWriter.blankLine();
        rightWriter.writeLine('  STARTING STATS', 'frostSilver');
        rightWriter.writeLine('  ────────────────', 'frostSilver');
        rightWriter.writeLine(`  HP:        ${cls.startingHP}`, 'white');
        rightWriter.writeLine(`  Primary:   ${cls.primaryStat.charAt(0).toUpperCase() + cls.primaryStat.slice(1)}`, 'green');
        rightWriter.blankLine();
        for (const stat of Object.keys(cls.startingStats) as StatName[]) {
          const val = cls.startingStats[stat];
          const abbr = STAT_ABBR[stat];
          const rating = getStatRating(val);
          const isPrimary = stat === cls.primaryStat;
          const color = isPrimary ? 'green' : 'white';
          rightWriter.writeLine(`  ${abbr}:  ${val.toString().padStart(2)}  ${rating}`, color as ColorName);
        }
      };

      // Draw initial preview for first class
      drawPreviewPanel(0);

      const leftMenuCfg = leftWriter.menuConfig();
      const selection = await menu.show(options, {
        showExit: true,
        exitLabel: 'Back to Title',
        startRow: leftMenuCfg.startRow,
        startCol: leftMenuCfg.startCol,
        maxWidth: leftMenuCfg.maxWidth,
        initialHighlightIndex: 0,
        onHighlight: drawPreviewPanel,
        onBeforeRender: () => {
          ctx.sprites.hide();
          const newLayout = frameRenderer.drawFrame({
            title: 'CREATE YOUR HERO',
            headerRight: 'Choose a Class',
            borderColor: 'frostBlue',
            twoColumn: true,
            leftRatio: 0.40,
          });

          // Redraw left panel header content
          const lw = frameRenderer.createWriter(newLayout.leftPanel!, effects);
          lw.blankLine();
          lw.writeCentered('CHOOSE YOUR CLASS', 'icyWhite');
          lw.blankLine();

          // Redraw preview for current highlight
          const idx = ctx.input.getHighlightIndex();
          if (idx >= 0 && idx < classIds.length) {
            drawPreviewPanel(idx);
          }

          // Return updated layout so MenuSystem can sync
          const menuCfg = lw.menuConfig();
          return { startRow: menuCfg.startRow, startCol: menuCfg.startCol, maxWidth: menuCfg.maxWidth };
        },
      });

      if (selection === -1) return null;

      // Show class detail
      const classId = classIds[selection];
      const action = await this.showClassDetail(engine, classId);
      if (action === 'choose') return classId;
      // 'back' -> loop to class menu
    }
  }

  private async showClassDetail(
    engine: GameEngine,
    classId: ClassId,
  ): Promise<'choose' | 'back'> {
    const ctx = engine.getContext();
    const { menu, effects, frameRenderer, sprites } = ctx;
    const cls = getClass(classId);

    ctx.sprites.hide();
    const layout = frameRenderer.drawFrame({
      title: cls.name.toUpperCase(),
      headerRight: cls.tagline,
      borderColor: 'frostBlue',
    });

    // Show class sprite with viewport-aware height
    const spriteKey = classId === 'frostblade_fighter' ? 'frostblade' : classId;
    const spriteHeight = ctx.viewport.getSpriteHeight();
    try {
      await sprites.drawInPanel(spriteKey, layout.content.startRow, layout.content.startCol, layout.content.width, spriteHeight);
    } catch (err) {
      console.warn(`Failed to load sprite for ${classId}:`, err);
      // Continue without sprite - not a critical failure
    }

    const writer = frameRenderer.createWriter(layout.content, effects);
    writer.blankLine();
    writer.writeLine(`  ${cls.symbol}  ${cls.name.toUpperCase()}`, cls.color as ColorName);
    writer.blankLine();

    // Description
    writer.writeLine(`  ${cls.description}`, 'white');
    writer.blankLine();

    // Stats
    writer.writeLine('  \u2500\u2500\u2500 Stats \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500', 'frostSilver');
    for (const stat of Object.keys(cls.startingStats) as StatName[]) {
      const val = cls.startingStats[stat];
      const abbr = STAT_ABBR[stat];
      const isPrimary = stat === cls.primaryStat;

      if (isPrimary) {
        writer.writeLine(`    ${abbr}  ${val}  \u2605 Primary`, 'green');
      } else {
        writer.writeLine(`    ${abbr}  ${val}`, 'white');
      }
    }
    writer.blankLine();

    // HP and Frostbite
    writer.writeLine(`  HP: ${cls.startingHP}  |  Frostbite Cost: ${cls.frostbiteCost}`, 'frostSilver');
    writer.blankLine();

    // Menu
    const options: MenuOption[] = [
      { label: 'Choose This Class', color: 'green' },
      { label: 'Back to Class List', color: 'amber' },
    ];

    const menuCfg = writer.menuConfig();
    const sel = await menu.show(options, {
      showExit: false,
      startRow: menuCfg.startRow,
      startCol: menuCfg.startCol,
      maxWidth: menuCfg.maxWidth,
    });

    if (sel === 0) return 'choose';
    return 'back';
  }

  // ─── Name Entry ──────────────────────────────────────────

  private async nameEntry(engine: GameEngine, classId: ClassId): Promise<string | null> {
    const ctx = engine.getContext();
    const { menu, effects, names, frameRenderer } = ctx;
    const cls = getClass(classId);

    let generated = names.heroName();

    while (true) {
      ctx.sprites.hide();
      const layout = frameRenderer.drawFrame({
        title: 'NAME YOUR HERO',
        headerRight: cls.name,
        borderColor: 'frostBlue',
      });

      const writer = frameRenderer.createWriter(layout.content, effects);
      writer.blankLine();
      writer.writeCentered(`Name your ${cls.name}`, 'frostSilver');
      writer.blankLine();
      writer.writeCentered(`Generated: ${generated}`, 'icyWhite');
      writer.blankLine();

      const options: MenuOption[] = [
        { label: `Accept "${generated}"`, color: 'green' },
        { label: 'Re-roll Name', color: 'amber' },
        { label: 'Enter Custom Name', color: 'amber' },
      ];

      const menuCfg = writer.menuConfig();
      const sel = await menu.show(options, {
        showExit: true,
        exitLabel: 'Back',
        startRow: menuCfg.startRow,
        startCol: menuCfg.startCol,
        maxWidth: menuCfg.maxWidth,
        onBeforeRender: () => {
          ctx.sprites.hide();
          frameRenderer.drawFrame({
            title: 'NAME YOUR HERO',
            headerRight: cls.name,
            borderColor: 'frostBlue',
          });
        },
      });

      if (sel === -1) return null;
      if (sel === 0) return generated;
      if (sel === 1) { generated = names.heroName(); continue; }

      // Manual entry
      ctx.sprites.hide();
      const entryLayout = frameRenderer.drawFrame({
        title: 'ENTER NAME',
        headerRight: cls.name,
        borderColor: 'frostBlue',
      });
      const entryWriter = frameRenderer.createWriter(entryLayout.content, effects);
      entryWriter.blankLine();
      entryWriter.writeLine('  Type a name and press Enter (Escape to go back):', 'frostSilver');
      entryWriter.blankLine();
      const promptRow = entryWriter.getCurrentRow();
      ctx.terminal.setCursor(promptRow, entryLayout.content.startCol);
      ctx.terminal.write('  > ', 'yellow');
      ctx.terminal.showCursor();

      const text = await new Promise<string>((resolve) => {
        const prevMode = ctx.input.getMode();
        ctx.input.setMode('text');
        const handler = (event: { type: string; text?: string }) => {
          if (event.type === 'textSubmit') {
            ctx.input.off(handler);
            ctx.input.setMode(prevMode);
            resolve(event.text ?? '');
          } else if (event.type === 'cancel') {
            ctx.input.off(handler);
            ctx.input.setMode(prevMode);
            resolve('');
          }
        };
        ctx.input.on(handler);
      });

      ctx.terminal.hideCursor();
      if (!text || text.trim().length < 2) continue;
      if (text.length > 20) {
        entryWriter.blankLine();
        entryWriter.writeLine('  Name must be 20 characters or fewer.', 'red');
        await effects.delay(1500);
        continue;
      }
      if (!/^[a-zA-Z\s'-]+$/.test(text)) {
        entryWriter.blankLine();
        entryWriter.writeLine('  Letters, spaces, hyphens, and apostrophes only.', 'red');
        await effects.delay(1500);
        continue;
      }

      // M8: Confirm the custom name before committing
      const trimmed = text.trim();
      ctx.sprites.hide();
      const confirmLayout = frameRenderer.drawFrame({
        title: 'CONFIRM NAME',
        headerRight: cls.name,
        borderColor: 'frostBlue',
      });
      const confirmWriter = frameRenderer.createWriter(confirmLayout.content, effects);
      confirmWriter.blankLine();
      confirmWriter.writeCentered(`Your hero will be named:`, 'frostSilver');
      confirmWriter.blankLine();
      confirmWriter.writeCentered(trimmed, 'icyWhite');
      confirmWriter.blankLine();

      const confirmOptions: MenuOption[] = [
        { label: `Accept "${trimmed}"`, color: 'green' },
        { label: 'Enter a different name', color: 'amber' },
      ];
      const confirmMenuCfg = confirmWriter.menuConfig();
      const confirmSel = await menu.show(confirmOptions, {
        showExit: false,
        startRow: confirmMenuCfg.startRow,
        startCol: confirmMenuCfg.startCol,
        maxWidth: confirmMenuCfg.maxWidth,
      });

      if (confirmSel === 0) return trimmed;
      // Otherwise loop back to entry
    }
  }

  // ─── Backstory Intro ─────────────────────────────────────

  private async backstoryIntro(engine: GameEngine, classId: ClassId, characterName: string): Promise<void> {
    const ctx = engine.getContext();
    const { effects, settings, frameRenderer } = ctx;
    const cls = getClass(classId);
    const speed = settings.getTypewriterSpeed();

    ctx.sprites.hide();
    const layout = frameRenderer.drawFrame({
      title: cls.name.toUpperCase(),
      headerRight: 'Backstory',
      borderColor: 'frostBlue',
    });

    const writer = frameRenderer.createWriter(layout.content, effects);
    writer.blankLine();
    writer.writeLine(`  ${cls.symbol}  ${characterName}, ${cls.name.toUpperCase()}`, cls.color as ColorName);
    writer.blankLine();

    // Render backstory using PanelWriter (frame-aware), interpolating character name
    const backstoryText = cls.backstoryIntro.replace(/\{characterName\}/g, characterName);
    const backstoryLines = backstoryText.split(/\n\n?/).filter((l: string) => l.trim().length > 0);
    if (speed > 0) {
      await writer.typewriterParagraph(backstoryLines, 'white', speed);
    } else {
      for (const line of backstoryLines) {
        writer.writeLine(line, 'white');
      }
    }

    await writer.pressAnyKey();
  }

  // ─── Create Character & Start Game ───────────────────────

  private async createAndStart(engine: GameEngine, classId: ClassId, characterName: string): Promise<void> {
    const ctx = engine.getContext();
    const { effects, storyEngine, characterSystem, saveSystem, abilitySystem, frameRenderer } = ctx;
    const cls = getClass(classId);

    // Show loading in frame
    ctx.sprites.hide();
    const layout = frameRenderer.drawFrame({
      title: 'CREATING CHARACTER...',
      borderColor: 'frostBlue',
    });
    const writer = frameRenderer.createWriter(layout.content, effects);
    const offset = writer.calculateVerticalOffset(3);
    writer.skip(offset);
    writer.writeCentered('Creating character...', 'frostSilver');

    // Create game state
    const gameState = characterSystem.createCharacter(classId, characterName);
    ctx.gameState = gameState;

    // Initialize ability tiers for the chosen class
    abilitySystem.initializeAbilities(classId, gameState);

    // Wire state into story engine and initialize node index
    storyEngine.setState(gameState);

    try {
      await effects.withLoading('  Loading story...', storyEngine.initialize());
    } catch (err) {
      console.error('Failed to load story data:', err);
      await engine.replaceScene(new ErrorScene('Failed to load story data. Please try again.'));
      return;
    }

    // Advance to the starting node
    storyEngine.advanceToNode(gameState.story.currentNodeId);

    // Auto-save to slot 0
    saveSystem.save(0, gameState, 'Prologue', cls.name);

    analytics.classSelection(cls.name);

    // Replace this scene with StoryScene
    await engine.replaceScene(new StoryScene());
  }

  // ─── Helper Methods ──────────────────────────────────────────

  /** Get rating label for stat value */
  private getStatRating(value: number): string {
    if (value >= 16) return '(Exceptional)';
    if (value >= 14) return '(Strong)';
    if (value >= 12) return '(Good)';
    if (value >= 10) return '(Average)';
    if (value >= 8) return '(Below Average)';
    if (value >= 6) return '(Poor)';
    return '(Very Poor)';
  }
}
